'use client';

import { useState } from 'react';
import Link from 'next/link';

export default function ContactSectionClient() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    question: '',
    agree: false,
  });
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSent(false);
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          question: formData.question,
          agree: formData.agree,
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to send your message right now.');
      }

      setSent(true);
      setFormData({
        name: '',
        email: '',
        question: '',
        agree: false,
      });
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to send your message right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Need help or want to share feedback? Reach out to the gamezo team anytime.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Support Email</p>
          <a href="mailto:support@gamezo.live" className="mt-1 block text-sm font-semibold text-primary hover:underline">
            support@gamezo.live
          </a>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Discord</p>
          <a href="https://discord.gg/gamezo" target="_blank" rel="noreferrer" className="mt-1 block text-sm font-semibold text-primary hover:underline">
            discord.gg/gamezo
          </a>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Social Media</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <a href="https://www.tiktok.com/@gamezo.live" target="_blank" rel="noreferrer" className="rounded-full border border-border px-3 py-1.5 text-sm hover:border-primary hover:text-primary">TikTok</a>
          <a href="https://www.youtube.com/@GameZOAZOUD-k8q" target="_blank" rel="noreferrer" className="rounded-full border border-border px-3 py-1.5 text-sm hover:border-primary hover:text-primary">YouTube</a>
          <a href="https://www.instagram.com/gamezo.live" target="_blank" rel="noreferrer" className="rounded-full border border-border px-3 py-1.5 text-sm hover:border-primary hover:text-primary">Instagram</a>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-bold text-foreground">Ask a question</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            value={formData.name}
            onChange={(event) => handleChange('name', event.target.value)}
            placeholder="Your name"
            className="h-11 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary"
            disabled={isSubmitting}
            required
          />
          <input
            type="email"
            value={formData.email}
            onChange={(event) => handleChange('email', event.target.value)}
            placeholder="Your email"
            className="h-11 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary"
            disabled={isSubmitting}
            required
          />
        </div>
        <textarea
          value={formData.question}
          onChange={(event) => handleChange('question', event.target.value)}
          placeholder="Your question"
          className="min-h-32 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          disabled={isSubmitting}
          required
        />

        <label className="flex items-start gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={formData.agree}
            onChange={(event) => handleChange('agree', event.target.checked)}
            className="mt-0.5"
            disabled={isSubmitting}
            required
          />
          <span>
            By clicking "Send" I agree to the{' '}
            <Link href="/page/terms" className="text-primary hover:underline">Terms of Use</Link>{' '}
            and the{' '}
            <Link href="/page/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          </span>
        </label>

        <button
          type="submit"
          disabled={isSubmitting}
          className="h-11 rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting ? 'Sending...' : 'Send'}
        </button>

        {sent ? <p className="text-sm text-emerald-500">Your message has been sent.</p> : null}
        {errorMessage ? <p className="text-sm text-rose-500">{errorMessage}</p> : null}
      </form>
    </div>
  );
}
